import {
  FilterOption,
  FilterTypes,
  FilterViewModel,
} from '../../../ViewModel/filterViewModel/filterViewModel';
import { isRequestedGender } from '../../../../../utils/genderSelection/types';

export const getSelectedOptionsFromList = (list: FilterOption[]) =>
  list.filter((option) => option.selected);

export const optionsHaveChildren = (options: FilterOption[]) =>
  options[0]?.children !== undefined;

export function getSelectedOptions({ options }: FilterViewModel) {
  return optionsHaveChildren(options)
    ? options.reduce(
        (acc, option) =>
          acc.concat(getSelectedOptionsFromList(option.children!)),
        [] as FilterOption[],
      )
    : getSelectedOptionsFromList(options);
}

/** A gender option carries the gender itself as its value. */
export const isGenderOption = ({ value }: FilterOption) =>
  isRequestedGender(value);

const getGenderOptions = (options: FilterOption[]) =>
  options.filter(isGenderOption);

export const getStaffGenderOptions = (filterViewModels: FilterViewModel[]) =>
  getGenderOptions(
    filterViewModels.find(({ id }) => id === FilterTypes.STAFF_MEMBER)
      ?.options ?? [],
  );

export const toggleOptionInList = (
  list: FilterOption[],
  clickedOption: FilterOption,
  maxOptionsToSelect: number | undefined = undefined,
) =>
  maxOptionsToSelect === 1
    ? [clickedOption]
    : clickedOption.selected
    ? list.filter(
        (option) => option.selected && option.value !== clickedOption?.value,
      )
    : list.filter(
        (option) => option.selected || option.value === clickedOption?.value,
      );

/** A gender is single-select and excludes named staff, in either direction. */
export const toggleFlatFilterOptions = (
  options: FilterOption[],
  clickedOption: FilterOption,
  maxOptionsToSelect: number | undefined = undefined,
): FilterOption[] => {
  if (isGenderOption(clickedOption)) {
    return clickedOption.selected ? [] : [clickedOption];
  }

  return toggleOptionInList(
    options.filter((option) => !isGenderOption(option)),
    clickedOption,
    maxOptionsToSelect,
  );
};

export const applyFilterOptionExclusivity = (
  options: FilterOption[],
  clickedOption: FilterOption,
) => {
  if (!clickedOption.selected) {
    return;
  }

  const clickedGender = isGenderOption(clickedOption);

  options.forEach((option) => {
    if (option !== clickedOption && (clickedGender || isGenderOption(option))) {
      option.selected = false;
    }
  });
};

export const buildFilterUpdate = ({
  id,
  selectedOptions,
}: {
  id: FilterTypes;
  selectedOptions: FilterOption[];
}): Partial<Record<FilterTypes, string[]>> => {
  if (id !== FilterTypes.STAFF_MEMBER) {
    return { [id]: selectedOptions.map(({ value }) => value) };
  }

  return {
    [FilterTypes.STAFF_MEMBER]: selectedOptions
      .filter((option) => !isGenderOption(option))
      .map(({ value }) => value),
    [FilterTypes.STAFF_GENDER]: selectedOptions
      .filter(isGenderOption)
      .map(({ value }) => value),
  };
};
